<template>
  <div class="art-placeholder" :class="placeholderClass" :style="placeholderStyle">
    <div class="art-content">
      <div v-if="showCategory" class="category-badge">{{ categoryName }}</div>
      <div class="art-pattern" :style="patternStyle"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ArtPlaceholder',
  props: {
    post: {
      type: Object,
      required: true,
    },
    showCategory: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    categoryName() {
      if (!this.post.category) return '';
      return typeof this.post.category.name === 'string' 
        ? this.post.category.name 
        : (this.post.category.name?.en || '');
    },
    categorySlug() {
      if (!this.post.category) return 'default';
      return this.post.category.slug || 'default';
    },
    placeholderClass() {
      return `placeholder-${this.categorySlug}`;
    },
    // Generate color scheme based on category or title hash
    colorScheme() {
      const schemes = {
        'ai': { primary: '#6366f1', secondary: '#8b5cf6', accent: '#a78bfa' },
        'machine-learning': { primary: '#3b82f6', secondary: '#60a5fa', accent: '#93c5fd' },
        'cybersecurity': { primary: '#ef4444', secondary: '#f87171', accent: '#fca5a5' },
        'blockchain': { primary: '#f59e0b', secondary: '#fbbf24', accent: '#fcd34d' },
        'space-technology': { primary: '#0ea5e9', secondary: '#38bdf8', accent: '#7dd3fc' },
        'quantum-computing': { primary: '#8b5cf6', secondary: '#a78bfa', accent: '#c4b5fd' },
        'iot': { primary: '#10b981', secondary: '#34d399', accent: '#6ee7b7' },
        'ar-vr': { primary: '#ec4899', secondary: '#f472b6', accent: '#f9a8d4' },
        'default': { primary: '#64748b', secondary: '#94a3b8', accent: '#cbd5e1' },
      };
      
      return schemes[this.categorySlug] || schemes['default'];
    },
    // Generate a hash from title for consistent color selection
    titleHash() {
      if (!this.post.title) return 0;
      let hash = 0;
      for (let i = 0; i < this.post.title.length; i++) {
        hash = ((hash << 5) - hash) + this.post.title.charCodeAt(i);
        hash = hash & hash;
      }
      return Math.abs(hash);
    },
    // Create gradient background
    placeholderStyle() {
      const colors = this.colorScheme;
      const angle = (this.titleHash % 360);
      
      return {
        background: `linear-gradient(${angle}deg, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.accent} 100%)`,
      };
    },
    // Create pattern overlay
    patternStyle() {
      const patternType = this.titleHash % 4;
      const colors = this.colorScheme;
      
      switch (patternType) {
        case 0:
          // Geometric circles
          return {
            backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.accent}40 0%, transparent 50%),
                             radial-gradient(circle at 80% 50%, ${colors.secondary}40 0%, transparent 50%),
                             radial-gradient(circle at 50% 80%, ${colors.primary}30 0%, transparent 50%)`,
            opacity: '0.6',
          };
        case 1:
          // Diagonal stripes
          return {
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, ${colors.accent}20 10px, ${colors.accent}20 20px)`,
            opacity: '0.4',
          };
        case 2:
          // Dots pattern
          return {
            backgroundImage: `radial-gradient(circle, ${colors.accent}30 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
            opacity: '0.5',
          };
        default:
          // Wave pattern
          return {
            backgroundImage: `repeating-linear-gradient(0deg, ${colors.primary}20, ${colors.secondary}20 2px, ${colors.accent}20 2px, ${colors.accent}20 4px)`,
            opacity: '0.3',
          };
      }
    },
  },
};
</script>

<style scoped>
.art-placeholder {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.art-content {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.category-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1f2937;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.art-pattern {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  mix-blend-mode: overlay;
}

/* Category-specific styles */
.placeholder-space-technology .art-pattern {
  background-image: 
    radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 70% 70%, rgba(255, 255, 255, 0.15) 0%, transparent 50%);
}

.placeholder-ai .art-pattern {
  background-image: 
    repeating-linear-gradient(45deg, transparent, transparent 15px, rgba(255, 255, 255, 0.1) 15px, rgba(255, 255, 255, 0.1) 30px);
}

.placeholder-cybersecurity .art-pattern {
  background-image: 
    repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(0, 0, 0, 0.1) 10px, rgba(0, 0, 0, 0.1) 20px);
}
</style>
